import * as React from 'react';
import { useState, useContext, useEffect } from 'react';
import {
    Box,
    Dialog,
    IconButton,
    Menu,
    MenuItem,
    useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { StorageContext } from '../../store/storageContext';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTranslation } from 'react-i18next';
import { getRowMenuPosition, type MenuPosition } from '../../utils/menuPosition';
import StorageForm from './StorageForm';
import DeleteWindow from './DeleteWindow';

interface Props {
    selectedSiteId: number | null;
    selectedStorageId: number | null;
    onStorageClick: (
        storageId: number | null,
        storageName?: string,
        storage?: StorageType | null
    ) => void;
}

const StorageColumn: React.FC<Props> = ({
    selectedSiteId,
    selectedStorageId,
    onStorageClick,
}) => {
    const {
        getStorageListBySite,
        storageListVersion,
        editStorage,
        editStorageLoading,
        deleteStorage,
        deleteStorageLoading,
    } = useContext(StorageContext);
    const [storages, setStorages] = useState<StorageType[]>([]);
    const [hoveredStorageId, setHoveredStorageId] = useState<number | null>(null);
    const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
    const [actionStorageId, setActionStorageId] = useState<number | null>(null);
    const [editing, setEditing] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const theme = useTheme();
    const { t } = useTranslation();

    const formatDate = (dateString: string | null) => {
        if (!dateString) {
            return '';
        }
        return new Intl.DateTimeFormat('fi-FI').format(new Date(dateString));
    };

    useEffect(() => {
        if (selectedSiteId === null) {
            setStorages([]);
            return;
        }

        getStorageListBySite(selectedSiteId, (result) => {
            setStorages(result);
        });
    }, [getStorageListBySite, selectedSiteId, storageListVersion]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, storageId: number) => {
        event.stopPropagation();
        setMenuPosition(getRowMenuPosition(event, '[data-storage-row="true"]'));
        setActionStorageId(storageId);
    };

    const handleMenuClose = () => {
        setMenuPosition(null);
    };

    const handleEditOpen = () => {
        setEditing(true);
        handleMenuClose();
    };

    const handleEditClose = () => {
        setEditing(false);
    };

    const handleDeleteOpen = () => {
        setDeleting(true);
        handleMenuClose();
    };

    const handleDeleteClose = () => {
        setDeleting(false);
    };

    const selectedStorage = actionStorageId
        ? storages.find((storage) => storage.id === actionStorageId)
        : undefined;

    const handleEditSubmit = (data: StorageFormDto) => {
        if (!selectedStorage) {
            return;
        }

        editStorage(selectedStorage.id, data, () => {
            setEditing(false);
        });
    };

    const handleDeleteConfirm = () => {
        if (!selectedStorage) {
            return;
        }

        deleteStorage(selectedStorage.id, () => {
            setDeleting(false);
            if (selectedStorageId === selectedStorage.id) {
                onStorageClick(null);
            }
        });
    };



    return (
        <>
            <Grid size={{ xs: 9 }} sx={{ borderLeft: theme.palette.border, display: 'flex' }}>
                <Grid size={{ xs: 6 }} sx={{ p: 1, display: 'flex', flexDirection: 'column' }}>
                    {storages.map((storage) => (
                        <Box
                            key={storage.id}
                            data-storage-row="true"
                            onMouseEnter={() => setHoveredStorageId(storage.id)}
                            onMouseLeave={() => setHoveredStorageId(null)}
                            onClick={() => onStorageClick(storage.id, storage.name, storage)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                minHeight: '30px',
                                px: 1.5,
                                mb: 1,
                                borderRadius: '4px',
                                cursor: 'pointer',
                                ...theme.typography.usersFontTitle,
                                backgroundColor:
                                    selectedStorageId === storage.id
                                        ? theme.palette.selectedBackground
                                        : 'transparent',
                                gap: 1,
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flexGrow: 1,
                                }}
                            >
                                {storage.name}
                            </Box>
                            {hoveredStorageId === storage.id && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => handleMenuOpen(e, storage.id)}
                                    sx={{ color: 'green', p: '4px' }}
                                >
                                    <MoreVertIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                    ))}
                </Grid>
                <Grid size={{ xs: 3 }} sx={{ p: 1, display: 'flex', flexDirection: 'column' }}>
                    {storages.map((storage) => (
                        <Box key={storage.id}>
                            {formatDate(storage.lastReportDate)}
                        </Box>
                    ))}
                </Grid>
                <Grid size={{ xs: 3 }} sx={{ p: 1, display: 'flex', flexDirection: 'column' }}>
                    {storages.map((storage) => (
                        <Box key={storage.id}>
                            {formatDate(storage.lastInventoryDate)}
                        </Box>
                    ))}
                </Grid>
            </Grid>
            <Menu
                anchorReference="anchorPosition"
                anchorPosition={menuPosition || undefined}
                open={Boolean(menuPosition)}
                onClose={handleMenuClose}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'left',
                }}
            >
                <MenuItem onClick={handleEditOpen}>{t('common:t.edit')}</MenuItem>
                <MenuItem onClick={handleDeleteOpen}>{t('common:t.delete')}</MenuItem>
            </Menu>

            <Dialog open={editing} onClose={handleEditClose}>
                {selectedStorage && (
                    <StorageForm
                        onSubmit={handleEditSubmit}
                        defaultValues={{
                            name: selectedStorage.name,
                            siteId: selectedStorage.siteId,
                            lastReportDate: selectedStorage.lastReportDate,
                            lastInventoryDate: selectedStorage.lastInventoryDate,
                        }}
                        buttonText={t('common:t.edit')}
                        loading={editStorageLoading}
                        cancelButton
                        onCancel={handleEditClose}
                    />
                )}
            </Dialog>
            <Dialog open={deleting} onClose={handleDeleteClose}>
                {selectedStorage && (
                    <DeleteWindow
                        onConfirm={handleDeleteConfirm}
                        onCancel={handleDeleteClose}
                        loading={deleteStorageLoading}
                    />
                )}
            </Dialog>
        </>
    );
};

export default StorageColumn;
